import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Filter, Layers, Search, Sparkles, X } from 'lucide-react';

export type SmartFilterContentScope = 'all' | 'findings' | 'empty';

export interface SmartFilterOption {
  id: string | number;
  label: string;
  count?: number;
}

export interface SmartViewerFilterValue {
  contentScope: SmartFilterContentScope;
  views: string[];
  structureIds: Array<string | number>;
  /** Optional study-level category groups (General Viewer). */
  groups?: string[];
}

export const EMPTY_SMART_FILTER: SmartViewerFilterValue = {
  contentScope: 'all',
  views: [],
  structureIds: [],
  groups: [],
};

export function isSmartFilterActive(filter: SmartViewerFilterValue): boolean {
  return (
    filter.contentScope !== 'all' ||
    filter.views.length > 0 ||
    filter.structureIds.length > 0 ||
    (filter.groups?.length ?? 0) > 0
  );
}

export function smartFilterActiveCount(filter: SmartViewerFilterValue): number {
  return (
    (filter.contentScope !== 'all' ? 1 : 0) +
    filter.views.length +
    filter.structureIds.length +
    (filter.groups?.length ?? 0)
  );
}

function Chip({
  selected,
  label,
  count,
  onClick,
}: {
  selected: boolean;
  label: string;
  count?: number;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`inline-flex max-w-full items-center gap-1 rounded-full border px-2.5 py-1 text-[11px] font-medium transition ${
        selected
          ? 'border-blue-500 bg-blue-600 text-white shadow-sm shadow-blue-600/20'
          : 'border-slate-200 bg-white text-slate-600 hover:border-blue-300 hover:bg-blue-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-300 dark:hover:border-blue-500'
      }`}
      title={label}
    >
      <span className="truncate">{label}</span>
      {typeof count === 'number' ? (
        <span
          className={`rounded-full px-1 text-[9px] font-bold ${
            selected ? 'bg-white/20 text-white' : 'bg-slate-100 text-slate-500 dark:bg-gray-700'
          }`}
        >
          {count}
        </span>
      ) : null}
    </button>
  );
}

export interface SmartViewerFilterProps {
  filter: SmartViewerFilterValue;
  onChange: (next: SmartViewerFilterValue) => void;
  viewOptions: SmartFilterOption[];
  structureOptions: SmartFilterOption[];
  groupOptions?: SmartFilterOption[];
  matchedCount?: number;
  totalCount?: number;
  showContentScope?: boolean;
  tip?: string;
  emptyViewsLabel?: string;
  emptyStructuresLabel?: string;
  emptyGroupsLabel?: string;
  matchLabel?: string;
  buttonLabel?: string;
  buttonHint?: string;
}

export function SmartViewerFilter({
  filter,
  onChange,
  viewOptions,
  structureOptions,
  groupOptions,
  matchedCount,
  totalCount,
  showContentScope = true,
  tip,
  emptyViewsLabel,
  emptyStructuresLabel,
  emptyGroupsLabel,
  matchLabel,
  buttonLabel,
  buttonHint,
}: SmartViewerFilterProps) {
  const { t } = useTranslation('GeneralViewer');
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const rootRef = useRef<HTMLDivElement>(null);
  const active = isSmartFilterActive(filter);
  const activeCount = smartFilterActiveCount(filter);
  const groups = filter.groups ?? [];

  const scopes = useMemo(
    () =>
      [
        { id: 'all' as const, label: t('smartFilter.scope.all', { defaultValue: 'All frames' }) },
        {
          id: 'findings' as const,
          label: t('smartFilter.scope.findings', { defaultValue: 'With findings' }),
        },
        {
          id: 'empty' as const,
          label: t('smartFilter.scope.empty', { defaultValue: 'Empty only' }),
        },
      ] as const,
    [t]
  );

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const q = query.trim().toLowerCase();
  const filteredViews = useMemo(
    () => (q ? viewOptions.filter((o) => o.label.toLowerCase().includes(q)) : viewOptions),
    [viewOptions, q]
  );
  const filteredStructures = useMemo(
    () => (q ? structureOptions.filter((o) => o.label.toLowerCase().includes(q)) : structureOptions),
    [structureOptions, q]
  );
  const filteredGroups = useMemo(
    () =>
      !groupOptions
        ? []
        : q
          ? groupOptions.filter((o) => o.label.toLowerCase().includes(q))
          : groupOptions,
    [groupOptions, q]
  );

  const toggleView = (code: string) => {
    const views = filter.views.includes(code)
      ? filter.views.filter((v) => v !== code)
      : [...filter.views, code];
    onChange({ ...filter, views });
  };

  const toggleStructure = (id: string | number) => {
    const asKey = String(id);
    const structureIds = filter.structureIds.some((x) => String(x) === asKey)
      ? filter.structureIds.filter((x) => String(x) !== asKey)
      : [...filter.structureIds, id];
    onChange({ ...filter, structureIds });
  };

  const toggleGroup = (id: string) => {
    const next = groups.includes(id) ? groups.filter((g) => g !== id) : [...groups, id];
    onChange({ ...filter, groups: next });
  };

  const resolvedButtonLabel = buttonLabel ?? t('topbar.filters', { defaultValue: 'Filter' });
  const resolvedButtonHint = buttonHint ?? t('smartFilter.buttonHint', { defaultValue: 'views' });
  const resolvedTip =
    tip ??
    t('smartFilter.tip', {
      defaultValue:
        'Tip: pick a view and/or structure to jump across matching frames only. Use the eye icons in Quality criteria to hide drawings without changing the frame filter.',
    });
  const resolvedEmptyViews =
    emptyViewsLabel ?? t('smartFilter.emptyViews', { defaultValue: 'No views available' });
  const resolvedEmptyStructures =
    emptyStructuresLabel ??
    t('smartFilter.emptyStructures', { defaultValue: 'No structures available' });
  const resolvedEmptyGroups =
    emptyGroupsLabel ?? t('smartFilter.emptyGroups', { defaultValue: 'No groups available' });

  const summary =
    matchLabel ??
    (matchedCount != null && totalCount != null
      ? t('smartFilter.showingFrames', {
          defaultValue: 'Showing {{matched}} of {{total}} frames',
          matched: matchedCount,
          total: totalCount,
        })
      : matchedCount != null
        ? t('smartFilter.matches', {
            defaultValue: '{{count}} matches',
            count: matchedCount,
          })
        : t('smartFilter.subtitle', { defaultValue: 'Filter by view or structure' }));

  return (
    <div ref={rootRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className={`flex h-8 items-center gap-1.5 rounded-lg border px-2.5 text-[11px] font-semibold transition-all ${
          open || active
            ? 'border-violet-600 bg-violet-600 text-white shadow-sm shadow-violet-600/25'
            : 'border-violet-200 bg-violet-50 text-violet-700 hover:border-violet-300 hover:bg-violet-100 dark:border-violet-800 dark:bg-violet-950/40 dark:text-violet-300'
        }`}
        title={t('smartFilter.title', { defaultValue: 'Smart filter' })}
      >
        <Filter size={12} />
        {resolvedButtonLabel}
        {active ? (
          <span className="min-w-[16px] rounded px-1 py-0.5 text-[9px] font-black bg-white/25 text-white">
            {activeCount}
          </span>
        ) : (
          <span className="hidden min-[420px]:inline text-[9px] font-medium opacity-70">
            {resolvedButtonHint}
          </span>
        )}
      </button>

      {open ? (
        <div className="absolute right-0 top-[calc(100%+6px)] z-50 w-[min(92vw,340px)] overflow-hidden rounded-xl border border-slate-200 bg-white shadow-xl shadow-slate-900/10 dark:border-gray-700 dark:bg-gray-900">
          <div className="flex items-center justify-between border-b border-slate-100 px-3 py-2.5 dark:border-gray-800">
            <div>
              <p className="text-xs font-bold text-slate-800 dark:text-gray-100">
                {t('smartFilter.title', { defaultValue: 'Smart filter' })}
              </p>
              <p className="text-[10px] text-slate-400">{summary}</p>
            </div>
            <div className="flex items-center gap-1">
              {active ? (
                <button
                  type="button"
                  onClick={() => onChange({ ...EMPTY_SMART_FILTER })}
                  className="rounded-md px-2 py-1 text-[10px] font-semibold text-violet-600 hover:bg-violet-50 dark:hover:bg-violet-900/20"
                >
                  {t('smartFilter.clear', { defaultValue: 'Clear' })}
                </button>
              ) : null}
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="flex h-6 w-6 items-center justify-center rounded-md text-slate-400 hover:bg-slate-100 dark:hover:bg-gray-800"
              >
                <X size={12} />
              </button>
            </div>
          </div>

          <div className="space-y-3 p-3">
            <div className="relative">
              <Search size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={t('smartFilter.searchPlaceholder', {
                  defaultValue: 'Search views or structures…',
                })}
                className="h-8 w-full rounded-lg border border-slate-200 bg-slate-50 pl-7 pr-2 text-[11px] text-slate-700 outline-none ring-blue-500 focus:ring-1 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-200"
              />
            </div>

            {showContentScope ? (
              <section>
                <div className="mb-1.5 flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-slate-400">
                  <Sparkles size={11} />
                  {t('smartFilter.quick', { defaultValue: 'Quick' })}
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {scopes.map((scope) => (
                    <Chip
                      key={scope.id}
                      selected={filter.contentScope === scope.id}
                      label={scope.label}
                      onClick={() => onChange({ ...filter, contentScope: scope.id })}
                    />
                  ))}
                </div>
              </section>
            ) : null}

            {groupOptions ? (
              <section>
                <div className="mb-1.5 flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-slate-400">
                  {t('filtersPanel.groups', { defaultValue: 'Groups' })}
                  <span className="font-medium normal-case tracking-normal text-slate-300">
                    {groupOptions.length}
                  </span>
                </div>
                {filteredGroups.length === 0 ? (
                  <p className="text-[11px] text-slate-400">{resolvedEmptyGroups}</p>
                ) : (
                  <div className="flex max-h-24 flex-wrap gap-1.5 overflow-y-auto">
                    {filteredGroups.map((opt) => (
                      <Chip
                        key={String(opt.id)}
                        selected={groups.includes(String(opt.id))}
                        label={opt.label}
                        count={opt.count}
                        onClick={() => toggleGroup(String(opt.id))}
                      />
                    ))}
                  </div>
                )}
              </section>
            ) : null}

            <section>
              <div className="mb-1.5 flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-slate-400">
                {t('filtersPanel.views', { defaultValue: 'Views' })}
                <span className="font-medium normal-case tracking-normal text-slate-300">
                  {viewOptions.length}
                </span>
              </div>
              {filteredViews.length === 0 ? (
                <p className="text-[11px] text-slate-400">{resolvedEmptyViews}</p>
              ) : (
                <div className="flex max-h-28 flex-wrap gap-1.5 overflow-y-auto">
                  {filteredViews.map((opt) => (
                    <Chip
                      key={String(opt.id)}
                      selected={filter.views.includes(String(opt.id))}
                      label={opt.label}
                      count={opt.count}
                      onClick={() => toggleView(String(opt.id))}
                    />
                  ))}
                </div>
              )}
            </section>

            <section>
              <div className="mb-1.5 flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-slate-400">
                <Layers size={11} />
                {t('smartFilter.structures', { defaultValue: 'Structures' })}
                <span className="font-medium normal-case tracking-normal text-slate-300">
                  {structureOptions.length}
                </span>
              </div>
              {filteredStructures.length === 0 ? (
                <p className="text-[11px] text-slate-400">{resolvedEmptyStructures}</p>
              ) : (
                <div className="flex max-h-32 flex-wrap gap-1.5 overflow-y-auto">
                  {filteredStructures.map((opt) => (
                    <Chip
                      key={String(opt.id)}
                      selected={filter.structureIds.some((x) => String(x) === String(opt.id))}
                      label={opt.label}
                      count={opt.count}
                      onClick={() => toggleStructure(opt.id)}
                    />
                  ))}
                </div>
              )}
            </section>

            <p className="rounded-lg bg-slate-50 px-2.5 py-2 text-[10px] leading-relaxed text-slate-500 dark:bg-gray-800/60 dark:text-gray-400">
              {resolvedTip}
            </p>
          </div>
        </div>
      ) : null}
    </div>
  );
}
